import { A, all, Div, Header, reactive, Section, Span, Variable, VBoolean } from 'alkali'
import { studyStats } from 'model/StudyCollection'
import { selectedOutcomes } from 'model/EvidenceSearch'
import { AnalysisSettings } from 'components/analysis/AnalysisSettings'
import { showLiveAnalysis, analysisType, analysisSelections, faqOrReadOnly, selectedStudies } from 'model/SearchQuery'
import { shortN, outcomeIntervalBar, default as Study } from '../studies/Study'
import { numToString, outcomeMeasureName } from 'model/AnalyzeStudy'
import { fullMeasureName } from 'analysis/Effect'
import { StudyGroups } from '../studies/StudyGroups'
import { GroupsNetwork, GroupsNetworkDialog } from './GroupsNetwork'
import { GroupingManagementDropdown } from './GroupingManagementDropdown'
import './Result.scss'
import { EvidenceStatement } from 'components/evidence/EvidenceStatement'
import { Toast } from 'components/generic/Toast'
const groupingDropdown = new GroupingManagementDropdown()

let evidenceToast
export default class Result extends Section('.study') {
	outcomeName: Variable
	timepointId: Variable
	outcome: Variable
	analyzable: VBoolean
	study: {}
	groupsNetwork: GroupsNetwork

	created(properties) {
		let { result } = properties
		let resultVisualizationElement
		let outcome = selectedOutcomes.valueOf()?.[0]
		if (result.error) {
			createMessage(result.error.message)
			return
		}
		if (!result.interventions)
			return
		if (!outcome) {
			createMessage('Please add an outcome to your question above or by using the filters below.')
			return
		}
		if (!result.interventions[0]) {
			/*if (studyStats.get('digitizedCount') > 1 && !analysisSelections.settings.get('useSMD') && selectedStudies.valueOf().length > 0 &&
					analysisType != 'bayesian' && !faqOrReadOnly.valueOf()) {
				createMessage(Span(['No analyzable studies/data selected, consider using ',
					A({
						href: '#',
						content: 'SMD',
						onclick(event) {
							event.preventDefault()
							new AnalysisSettings().show()
						}
					})]))
			} else*/
			createMessage('No analyzable studies/data selected')
			return
		}
		let N = result.N
		let amValue, isPositive
		let studyGroups
		let digitizedStudies = 0
		let publishedStudies = reactive(0)
		let groups = []
		let { min, max, scale } = result
		this.scale = scale
		const getAMValue = ({ effect, lowerBound, upperBound, isCohort  }) => {
			let settings = analysisSelections.settings.valueOf()
			let positiveDirectionality = Boolean(outcome.isPositive)
			let convert, noEffect
			if (outcome.type == 'Binary') {
				let measureBin = settings?.measureBin || 'RR'
				let isRatio = measureBin != 'RD'
				if (isCohort && measureBin == 'RR' || measureBin == 'RD') {
					convert = (effect) => ((isRatio ? Math.exp(effect) : effect) * 100).toFixed(2)
				} else {
					convert = (effect) => (isRatio ? Math.exp(effect) : effect).toFixed(2)
				}
				noEffect = 1
			} else if (outcome.type == 'Incidence' && result.interventions.length > 1) {
				convert = (effect) => Math.exp(effect).toFixed(2)
				noEffect = 1
			} else {
				convert = effect => effect.toFixed(2)
				noEffect = 0
			}
			amValue = convert(effect)
			isPositive = amValue > noEffect == positiveDirectionality
			return {
				amValue,
				lower: convert(lowerBound),
				upper: convert(upperBound),
				isPositive,
			}
		}
		if (result.isForestPlot) { // direct value, show relative-effects in a forest-plot
			let intervention = result.interventions.find(intervention => !intervention.isBaseline)
			let { effect, lowerBound, upperBound } = intervention
			amValue = getAMValue(intervention).amValue

			let diamondTop,
				diamondBottom
			resultVisualizationElement = new Div('.diamond', {
				left: (scale > 0 ? lowerBound : upperBound) * scale + 40 + '%',
			},
				[
					(diamondTop = new Div('.diamond-top')),
					(diamondBottom = new Div('.diamond-bottom')),
				]
			)

			setTimeout(() => {
				let cellWidth = resultVisualizationElement.parentNode.offsetWidth
				let rangeWidth = Math.max(Math.abs((upperBound - lowerBound) / 2 * scale * cellWidth) / 100, 5) + 'px'
				diamondTop.style.borderLeftWidth = diamondTop.style.borderRightWidth =
					diamondBottom.style.borderLeftWidth = diamondBottom.style.borderRightWidth = rangeWidth
			}, 20)
			for (let key in result) {
				if (key.startsWith(outcome.id)) {
					let effect = result[key]
					digitizedStudies = Math.max(
						digitizedStudies,
						Object.keys(effect.studies).length
					)
				}
			}
			if (result.studyIds)
				// direct meta results
				digitizedStudies = result.studyIds.length
			publishedStudies = reactive('?')//getArticleCountForIntervention(result.id || result.name, outcome.id.split(',')[0]).to((publishedData) =>
				//Math.max(publishedData?.count || 0, digitizedStudies))
		} else if (result.interventions?.length > 0) { // has groups, show absolute effects in a bar-chart
			let min = Infinity
			let max = -Infinity
			groups = this.groups = (result.interventions || []).map(group => {
				group.OriginalName = group.name
				group.analyzeAs = group.name
				if (group.value > max)
					max = group.value
				if (group.value < min)
					min = group.value
				return group
			})
			if (analysisSelections.settings.get('useSMD')) {
				outcome = Object.assign({}, outcome, {
					minMax: { min: Math.min(min, -1), max: Math.max(max, 1) },
					unit: '',
					type: 'Continuous',
				})
			}
			studyGroups = new StudyGroups({
				groups,
				outcome,
				getOutcomeValue: (group) => selectedOutcomes.to(() => group),
				groupAnalyzeAs: new Map(),
				effectElement: group => {
					let { amValue, upper, lower } = getAMValue(group)
					let confidenceLevel = analysisSelections.get('settings')?.confidenceLevel || 95
					let measure = outcomeMeasureName(outcome)
					measure = fullMeasureName[measure] || measure
					let baseline = analysisSelections.baseline.valueOf() || 'Control'
					let tooltip = group.analyzeAs == baseline ? (baseline == 'Control' ?
							'Control group is assumed to have no treatment effect.' :
							(baseline + ' set as baseline.')) + ' All ' + measure.valueOf().toLowerCase() + ' values are relative to ' + baseline + '.' :
						'Estimated ' + measure.valueOf().toLowerCase() + ' of ' + amValue + ' (' + lower + ' to ' + upper + ', ' + confidenceLevel + '% CI) vs ' + baseline
					return Div('.study-report', [
						Div('.study-report-treatment-circle', {
							classes: {
								'positive-effect': isPositive,
							},
						}),
						Div('.study-report-treatment-value', {
							classes: { 'positive-effect': isPositive },
							content: numToString(amValue),
							title: tooltip,
						}),
						Div('.forest-axis'),
						Div('.study-group-count', {
							content: group.studyCount,
							title:
								group.studyCount +
								(group.studyCount == 1 ? ' study' : ' studies') +
								' contributed to this arm/group of the meta analysis',
						}),
					])
				},
				selectGroup(event, group, value) {
					if (!group.isBaseline)
						analysisSelections.baseline.put(group.analyzeAs)
				},
				groupHover(group, event, element) {
					evidenceToast = new Toast(`.top-arrow.continuity-toast.active.hover-toast`, {
						id: Math.random().toString().slice(2),
						style: { position: 'absolute', left: (event.clientX - 400) + 'px', top: (event.clientY + 15) + 'px' },
						hover: true,
						hoverClose: true,
						display: true,
						title: ' ',
						message: group.isBaseline ?
							Div([group.analyzeAs + ' is the baseline treatment arm, and all other treatments are compared to this one']) :
							new Div([
								new EvidenceStatement({
									arm: group.analyzeAs,
								}),
								Div(['(Click on ' + group.analyzeAs + ' to select it as the baseline treatment arm)'])
							])
					})
					document.body.append(evidenceToast)
				},
				groupHoverClose() {
					document.body.removeChild(evidenceToast)
				},
				groupMenu(event, group, value) {
					groupingDropdown.setGrouping(group.analyzeAs)
					groupingDropdown.show(event)
				}
			})
		} else {
			createMessage('No analyzable studies/data available')
			return
		}
		function createMessage(message) {
			properties.classes = {
				'no-analysis-available': true,
				expanded: showLiveAnalysis,
			}
			properties.content = [ Div([ message ]) ]

		}
		properties.classes = {
			'positive-directionality': outcome.isPositive,
			expanded: showLiveAnalysis,
		}
		let resultRow = this

		properties.content = [
			Header('.study-head', [
				Div('.study-head-item.study-toggle', [
				]),
				Div('.study-head-item.study-year-name', [
					Span('.results-summary-title', [ 'Summary' ]),
					Span('.network-map-cell',{
						title: result.name ?
							'Estimated relative effects of ' + result.name :
							'A treatment network showing the direct treatment comparisons within selected studies. Click to see additional information on the network and treatment grouping.',
						content: result.name || (analysisType == 'cohort' ? null : GroupsNetwork.with({
							graphWidth: 300,
							graphHeight: Math.min(groups.length, 4) * 35,
							hideGroupCheckbox: true,
							onlyFirstLetters: true,
							onclick() {
								new GroupsNetworkDialog({
								}).show(event)
							},
							attached() {
								resultRow.groupsNetwork = this
							}
						})),
					}),
				]),
				Div('.study-head-item.study-N', {
					dataset: { N: shortN(N) }
				}),
				Div('.study-head-item.study-title', [
/*					result.interventions ? Div('.study-head-item-title', ['Estimated outcomes (based on relative effects)']) : null,*/
					Div('.study-report', [
						resultVisualizationElement && Div('.outcome-column', [
							Div('.study-report-treatment-value', { 
								title: 
									numToString(amValue) === '?' 
										? 'Effect size is not calculated when both values are zero.'
										: ''
						
							}, [ numToString(amValue) ]),
							resultVisualizationElement,
							Div('.forest-axis'),
							Div('.study-group-count', {
								content: digitizedStudies,
								title: digitizedStudies + (digitizedStudies == 1 ? ' study' : ' studies') + ' contributed to this meta analysis'
							})
						]),
					])
				]),
				Div('.study-head-item.study-design'),
			]),
			Div('.study-body', [
				Div('.study-body-meta'),
				Div('.study-body-Ns', groups.map((group, groupIndex) => {
					let { N, name } = group
					return Div('.study-group-N', {
						dataset: {
							N: shortN(N)
						},
						title: `There were ${N} total participants in the ${name} groups across the analyzed studies`, 
						style: {
							'--group-distance': groupIndex * 2.2 + 'em'
						}
					})
				})),
				studyGroups,
			])
		]
	}
	switchToForestPlot() {
		this.groups?.forEach((group, i) => {
			let position = group.effect * this.scale
			let size = group.isBaseline ? 0 : (Math.pow(group.N, 0.3) / 2 + 2) / 8

			let confidenceIntervalWidth = group.isBaseline ? 0 : Math.abs((group.upperBound - group.lowerBound) * this.scale / 2)
			let comparativeEffectCircle = this.querySelectorAll('.study-report-treatment-circle')[i]
			comparativeEffectCircle.style.left = 'calc(' + (position + 40) + '%' + ' - ' + (size / 2) + 'rem)'
			comparativeEffectCircle.style.height = comparativeEffectCircle.style.width = size + 'rem'
			comparativeEffectCircle.style.top = (1.5 - size / 2) + 'rem'

			let intervalBar = this.querySelectorAll('.study-group')[i].append(outcomeIntervalBar(confidenceIntervalWidth))
			intervalBar.style.left = (position + 40 - confidenceIntervalWidth) + '%'
		})
		/*if (this.groupsNetwork) {
			setTimeout(() => {
				let svg = this.groupsNetwork.firstChild
				svg.setAttribute('width', '400px')
				svg.setAttribute('height', '400px')
				let studiesPane = this.parentNode.parentNode.parentNode.parentNode
				studiesPane.insertBefore(this.groupsNetwork, studiesPane.lastChild)
			}, 500)
		}*/
	}
	switchToSourceView() {
		//this.querySelector('.study-N').dataset.N = this.study.N
		for (let comparativeEffectCircle of  this.querySelectorAll('.study-report-treatment-circle')) {
			comparativeEffectCircle.style.left = comparativeEffectCircle.style.top = ''
			comparativeEffectCircle.style.height = comparativeEffectCircle.style.width = ''
		}
		for (let effectBar of this.querySelectorAll('.confidence-bar')) {
			effectBar?.parentNode?.removeChild(effectBar)
		}/*
		if (this.groupsNetwork) {
			let svg = this.groupsNetwork.firstChild
			svg.setAttribute('width', '140px')
			svg.setAttribute('height', (this.groups.length * 25) + 'px')
			this.querySelector('.study-name span').appendChild(this.groupsNetwork)
		}*/
	}

}
